import React, { useCallback, useEffect, useEffectEvent, useMemo } from 'react'

import { useAuth } from '@affiliate-cabinet/auth'
import {
  I18nDictionaryLanguageKeysType,
  i18nLanguagesKeys,
  i18nLanguagesMap,
  i18nLanguagesNameMap,
  languagesShortMap,
  transformCabinetTranslationKeyToCoreTranslationKey,
  useI18n,
} from '@affiliate-cabinet/i18n'
import { onChangeLocaleHandler, useMutation } from '@affiliate-cabinet/rest'
import { CoreTypesComponents, ResponseErrorInterface } from '@affiliate-cabinet/types'
import { getFlagsPath } from '@affstore-ds/flags'
import { Avatar, ButtonVariantEnum, Dropdown, DropDownSizeEnum, getDefaultZIndex, mediaHelper, Text, useMediaQuery } from '@affstore-ds/uikit'

import { featuresStatuses, getBrandConfig } from '../../config/brands'
import { LANGUAGES_TO_FLAGS_MAP } from './base/constants'
import { LanguageDropdownPropertiesInterface } from './LanguageDropdown.types'

import { useLanguageDropdownStyles } from './LanguageDropdown.styled'

const { brandConfig } = getBrandConfig()

export const LanguageDropdown = ({ type = 'cabinet' }: LanguageDropdownPropertiesInterface) => {
  const { i18n } = useI18n()
  const {
    auth: { isUserLoading, user, userRefetch },
  } = useAuth()
  const { classes } = useLanguageDropdownStyles()

  const languageKey = useMemo(() => i18n.locale() as I18nDictionaryLanguageKeysType, [i18n])
  const userLocaleKey = useMemo(() => user?.ui_language?.split('_')[0] as I18nDictionaryLanguageKeysType, [user?.ui_language])
  const allowedLanguages = useMemo(
    () =>
      i18nLanguagesKeys.filter((language) => {
        if (featuresStatuses.isArabicLocaleEnabled) {
          return brandConfig.languages.includes(language)
        }

        return language !== languagesShortMap.ar && brandConfig.languages.includes(language)
      }),
    [],
  )
  const isGreaterThenLg = useMediaQuery(mediaHelper.greaterThen('lg', false))

  const onChangeLocaleHandlerMutation = useMutation<
    unknown,
    ResponseErrorInterface,
    Pick<CoreTypesComponents['schemas']['UpdateProfileQueryDto'], 'locale'>
  >(onChangeLocaleHandler)

  const handleClick = useCallback(
    (langKey: I18nDictionaryLanguageKeysType) => () => {
      i18n.handleLocale(langKey)

      if (user && type === 'cabinet') {
        onChangeLocaleHandlerMutation.mutate(
          {
            locale: transformCabinetTranslationKeyToCoreTranslationKey[langKey] || i18nLanguagesMap[langKey],
          },
          {
            onSuccess: () => {
              userRefetch()
              i18n.handleLocale(langKey)
            },
          },
        )
      }
    },
    [i18n, onChangeLocaleHandlerMutation, type, user, userRefetch],
  )

  const handleLocaleLoad = useEffectEvent(() => {
    if (!isUserLoading && userLocaleKey && userLocaleKey !== languageKey) {
      i18n.handleLocale(userLocaleKey)
    }
  })

  useEffect(() => {
    handleLocaleLoad()
  }, [])

  return (
    <Dropdown
      data-testid="dropdown-language"
      classNames={{
        targetElement: classes.targetElement,
      }}
      targetElementProps={{
        children: (
          <Text visibleFrom="lg" inherit>
            {i18nLanguagesNameMap[languageKey] as string}
          </Text>
        ),
        classNames: {
          label: classes.targetElementLabel,
          section: classes.targetElementSection,
        },
        elementVariant: ButtonVariantEnum.TertiaryMain,
        isRound: true,
        leftSection: <Avatar avatarSize={isGreaterThenLg ? 20 : 24} src={getFlagsPath(LANGUAGES_TO_FLAGS_MAP[languageKey])} />,
      }}
      elementSize={type === 'guest' ? DropDownSizeEnum.S : DropDownSizeEnum.M}
      zIndex={getDefaultZIndex('popover')}
    >
      <Dropdown.Items>
        {allowedLanguages.map((langKey) => (
          <Dropdown.Item
            key={langKey}
            data-testid={`dropdown-language-item-${langKey}`}
            data-current={langKey === languageKey}
            leftSection={<Avatar avatarSize={16} src={getFlagsPath(LANGUAGES_TO_FLAGS_MAP[langKey])} />}
            onClick={handleClick(langKey)}
          >
            {i18nLanguagesNameMap[langKey]}
          </Dropdown.Item>
        ))}
      </Dropdown.Items>
    </Dropdown>
  )
}
